import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QR_SERVICES } from '../../constants';
import { useAppStore } from '../../hooks/useAppStore';
import { CancelButton } from '../../../modules/order/components/CancelButton/CancelButton';
import { CancelPage } from '../../../modules/event-log';
import { useFeedbackActions } from '../../../modules/feedback/hooks/useFeedbackActions';
import { useReceiptActions } from '../../../modules/service/hooks/useReceiptActions';
import { useVmFeatures } from '../../hooks/useVmFeatures';
import { getVmDetails } from '../../lib/general';
import { ButtonBox } from '../../ui/ButtonBox/ButtonBox';
import { PrimaryButton } from '../../ui/PrimaryButton/PrimaryButton';
import { Button, SmallButton } from '../../ui/Button/Button';
import { SecondaryActions } from './SecondaryActions';

const MARGIN_RIGHT = { marginRight: '2px' } as const;
const MARGIN_LEFT = { marginLeft: '2px' } as const;

interface FeedbackAndReceiptProps {
    screenName: CancelPage;
    /** 'secondary' demotes both actions to text buttons — see SWRN-1800. */
    variant?: 'default' | 'secondary';
    /** Secondary variant only: drops the feedback action while the buyer is entering an amount (CT5). */
    isFeedbackHidden?: boolean;
}

const FeedbackAndReceipt = ({ screenName, variant = 'default', isFeedbackHidden = false }: FeedbackAndReceiptProps) => {
    const { t } = useTranslation();
    const vm = useAppStore((s) => s.vm);
    const { showFeedbackGlobal, showReceiptGlobal } = useVmFeatures();
    const { onFeedbackClickHandler } = useFeedbackActions();
    const { onReceiptCapcha } = useReceiptActions();

    const [isReceiptActive, setIsReceiptActive] = useState<boolean>(
        Boolean(showReceiptGlobal),
    );
    const [isFeedbacksActive, setIsFeedbacksActive] = useState<boolean>(
        Boolean(showFeedbackGlobal),
    );
    const { isCT5, isAllowed } = getVmDetails(vm);

    useEffect(() => {
        if (vm.QRservices === QR_SERVICES.SEPARATED_SERVICES && isAllowed) {
            setIsFeedbacksActive(false);
            setIsReceiptActive(false);
        }
    }, []);

    if (variant === 'secondary') {
        return (
            <SecondaryActions
                isFeedbackActive={isFeedbacksActive && !isFeedbackHidden}
                isReceiptActive={isReceiptActive}
                onFeedbackClick={onFeedbackClickHandler}
                onReceiptClick={onReceiptCapcha}
            />
        );
    }

    return (
        <ButtonBox>
            {isFeedbacksActive && (
                <PrimaryButton type="button" onClick={onFeedbackClickHandler}>
                    {t('Feedback')}
                </PrimaryButton>
            )}

            {isReceiptActive && !isFeedbacksActive ? (
                <Button onClick={onReceiptCapcha}>
                    {t('Get fiscal receipt')}
                </Button>
            ) : (
                isReceiptActive &&
                (!isCT5 ? (
                    <SmallButton
                        onClick={onReceiptCapcha}
                        style={MARGIN_RIGHT}
                    >
                        {t('Get fiscal receipt')}
                    </SmallButton>
                ) : (
                    <Button onClick={onReceiptCapcha}>
                        {t('Get fiscal receipt')}
                    </Button>
                ))
            )}

            {isReceiptActive && isFeedbacksActive
                ? !isCT5 && (
                      <CancelButton
                          page={screenName}
                          small
                          style={MARGIN_LEFT}
                      />
                  )
                : !isCT5 && <CancelButton page={screenName} />}
        </ButtonBox>
    );
};
export { FeedbackAndReceipt };
